import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VictoryLabel } from 'victory';

import { Toggle } from '~/components/Toggle';
import { cn } from '~/lib/cn';
import { LineGraphLegend } from '~/modules/artist-roster/components/LineGraph/components/LineGraphLegend';
import type { StreamType } from '~/modules/artist-roster/components/LineGraph/LineGraph';
import { LineGraph } from '~/modules/artist-roster/components/LineGraph/LineGraph';
import { getTrackPerformanceOverTime } from '~/modules/artist-roster/lib/tracks';
import { PerformanceModuleHeader } from '~/modules/artist-roster/pages/track-details/components/PerformanceModules/PerformanceModuleHeader';
import { formatMillionsNumber } from '~/modules/artist-roster/utils/formatMillionsNumber';

import { TrackStreamsGraphTooltip } from './components/TrackStreamsGraphTooltip';

// @TODO WMG1-409 Convert colors to tokens.
const LINE_COLORS = [
  'oklch(57.7% 0.2144 260.86)',
  'oklch(83.9% 0.1673 82.66)',
  'oklch(70.1% 0.2435 344.02)',
  'oklch(90.55% 0.1466 184.72)',
  'oklch(60.53% 0.2701 306.02)',
];

const TOOLTIP_OFFSETS = [
  [5, -25],
  [5, -42],
];

interface TrackStreamsGraphProps {
  familyId: string;
  territory: string;
  startDate: string;
  endDate: string;
  streamType: StreamType;
  className?: string;
}

export function TrackStreamsGraph({
  familyId,
  territory,
  startDate,
  endDate,
  streamType,
  className,
}: TrackStreamsGraphProps) {
  const { t } = useTranslation('roster');
  const [isCumulative, setIsCumulative] = useState(false);

  const { data } = useQuery({
    queryKey: ['track-performance-over-time', familyId],
    queryFn: () => {
      return getTrackPerformanceOverTime({ familyId });
    },
  });

  const metrics = useMemo(() => {
    if (!data) {
      return null;
    }

    if (streamType === 'streams') {
      let sum = 0;
      const streamsResult = [];

      for (let x = 0; x < data.performance_over_time.metrics.length; x++) {
        const value = data.performance_over_time.metrics[x];

        sum += value.streams;
        streamsResult.push({
          date: value.date,
          x,
          y: isCumulative ? sum : value.streams,
        });
      }

      return [streamsResult];
    }

    let sumOrganicSearch = 0;
    let sumOrganicLibrary = 0;
    const organicStreamsLibraryResult = [];
    const organicStreamsSearchResult = [];

    for (let x = 0; x < data.performance_over_time.metrics.length; x++) {
      const value = data.performance_over_time.metrics[x];
      const yValueOrganicLibrary = value.organic_streams.library.value;
      const yValueOrganicSearch = value.organic_streams.search.value;

      sumOrganicLibrary += yValueOrganicLibrary;
      sumOrganicSearch += yValueOrganicSearch;

      organicStreamsLibraryResult.push({
        date: value.date,
        x,
        y: isCumulative ? sumOrganicLibrary : yValueOrganicLibrary,
        delta: value.organic_streams.library.delta,
      });
      organicStreamsSearchResult.push({
        date: value.date,
        x,
        y: isCumulative ? sumOrganicSearch : yValueOrganicSearch,
        delta: value.organic_streams.search.delta,
      });
    }

    return [organicStreamsLibraryResult, organicStreamsSearchResult];
  }, [data, isCumulative, streamType]);

  if (!metrics) {
    return null;
  }

  const legendItems = [
    {
      lineColor: LINE_COLORS[0],
      label: t('trackDetails.labels.streamsRTD'),
    },
  ];

  const organicLegendItems = [
    {
      lineColor: LINE_COLORS[0],
      label: t('trackDetails.labels.library'),
    },
    {
      lineColor: LINE_COLORS[1],
      label: t('trackDetails.labels.search'),
    },
  ];

  return (
    <div
      className={cn(
        'rounded-sm border-px border-solid p-md bg-level-01 border-emphasis-subtle',
        className,
      )}
    >
      <PerformanceModuleHeader
        header={
          streamType === 'streams'
            ? t('trackDetails.streamsOverTime')
            : t('trackDetails.organicStreamsOverTime')
        }
        territory={territory}
        startDate={startDate}
        endDate={endDate}
      />
      <div className="flex items-center justify-between">
        <LineGraphLegend
          items={streamType === 'streams' ? legendItems : organicLegendItems}
        />
        <div className="flex items-center gap-sm text-body-sm font-semibold">
          <div className="pr-sm text-subtle">
            {t('trackDetails.labels.currentMonth')}
            <span className="font-normal">
              {format(new Date(), 'MMMM yyyy')}
            </span>
          </div>
          {isCumulative
            ? t('trackDetails.views.cumulative')
            : t('trackDetails.views.noncumulative')}
          <Toggle pressed={isCumulative} onPressedChange={setIsCumulative} />
        </div>
      </div>
      <LineGraph
        streamType={streamType}
        lineColors={LINE_COLORS}
        xAxisProps={{
          style: {
            tickLabels: {
              textAlign: 'left',
            },
          },
          domain: [0, metrics[0].length],
          tickLabelComponent: <VictoryLabel textAnchor="start" />,
          tickValues: metrics[0].map((datum) => datum.x),
          tickFormat: metrics[0].map((datum) =>
            format(parseISO(datum.date), 'MMMM d, yyyy'),
          ),
        }}
        yAxisProps={{
          orientation: 'right',
          label: t('trackDetails.labels.streamsRTD'),
          tickFormat: (num) => formatMillionsNumber(num, true),
        }}
        data={metrics}
        containerClassName="w-full"
        tooltip={<TrackStreamsGraphTooltip />}
        tooltipOffset={
          streamType === 'streams' ? TOOLTIP_OFFSETS[0] : TOOLTIP_OFFSETS[1]
        }
      />
    </div>
  );
}

TrackStreamsGraph.displayName = 'TrackStreamsGraph';
